/**
 * limitClauseKeyword — the keyword the one-line LIMIT row is headed with
 * (`LimitOffsetPanel`), so the heading matches what the statement says.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { ClauseSpan, SelectOutline } from './extractSelectOutline';

function spanText(bufferText: string, span: ClauseSpan | null): string {
  return span ? bufferText.slice(span.start, span.end).trim() : '';
}

/** A plain `FIRST n ROW(S) ONLY` tail — shown as just `n` under a "Fetch first" heading. */
export const PLAIN_FETCH_FIRST = /^FIRST\s+(\d+)\s+ROWS?\s+ONLY$/i;

/**
 * The keyword the LIMIT row is headed with. A present clause names itself
 * (TOP / LIMIT / FETCH FIRST; an OFFSET…FETCH pair gets "Offset / fetch");
 * with no clause yet, the keyword the engine's "Add limit" writes — TOP on SQL
 * Server, FETCH FIRST on Oracle, LIMIT elsewhere (`modify-limit` synthesis).
 */
export function limitClauseKeyword(
  outline: Pick<SelectOutline, 'top' | 'limit' | 'offset' | 'fetch'>,
  engine: SqlEngine,
  bufferText: string,
): string {
  if (outline.top) return 'Top';
  if (outline.limit) return 'Limit';
  if (outline.fetch && !outline.offset && PLAIN_FETCH_FIRST.test(spanText(bufferText, outline.fetch))) {
    return 'Fetch first';
  }
  if (outline.fetch || outline.offset) return 'Offset / fetch';
  if (engine === 'mssql') return 'Top';
  if (engine === 'oracle') return 'Fetch first';
  return 'Limit';
}

/** The statement has a TOP / LIMIT / OFFSET / FETCH. */
export function hasLimitClause(outline: SelectOutline): boolean {
  return !!(outline.top || outline.limit || outline.offset || outline.fetch);
}
